// src/ConsumerApp/Screens/Account/components/RfqCard.js

import React, { useState } from 'react';
import { theme } from '../../../../assets/theme/theme';

// Real SVGs from assets/icons
import ClipboardText from '../../../../assets/icons/PClipboardText.svg';
import ShootingStar from '../../../../assets/icons/ShootingStar.svg';
import Stack from '../../../../assets/icons/Stack.svg';
import CaretRightRed from '../../../../assets/icons/CaretRightRed.svg';
import ToteIcon from '../../../../assets/icons/tote.svg';

const IMAGE_BASE_URL =
  process.env.REACT_APP_FORE_BRICKBUCKET_IMAGES || 'https://assets.brickbucket.in/';

export default function RfqCard({ rfq, onPress }) {
  const [imgError, setImgError] = useState(false);

  const rfqNumber = rfq.rfq_number || `#${String(rfq._id || rfq.id || '').slice(-6).toUpperCase()}`;

  const isOrderPlaced =
    rfq.status === 'Order Placed Sucessfully' ||
    rfq.status === 'Order Placed Successfully';
  const isQuoteReceived = Boolean(rfq.quotation_id || rfq.is_live_rfq);

  const statusName = isOrderPlaced
    ? 'Order Placed'
    : isQuoteReceived
    ? 'Quote Received'
    : rfq.status || 'Submitted';

  const statusBg = isOrderPlaced ? '#E8F5EE' : isQuoteReceived ? '#FFF1F2' : '#F8FAFC';
  const statusColor = isOrderPlaced ? '#1E8449' : isQuoteReceived ? '#C7061F' : '#64748B';
  const StatusIcon = isOrderPlaced ? Stack : isQuoteReceived ? ShootingStar : ClipboardText;

  const formattedDate = rfq.createdAt
    ? new Date(rfq.createdAt).toLocaleString('en-IN', {
        day: '2-digit',
        month: 'short',
        year: 'numeric',
        hour: 'numeric',
        minute: '2-digit',
        hour12: true,
      })
    : '--';

  const displayTitle =
    rfq.product_categories?.[0]?.product_category_name ||
    rfq.title ||
    (rfq.items && rfq.items[0]?.item_name) ||
    'Bulk Procurement Request';

  const itemCount = rfq.total_items ?? rfq.items?.length ?? 1;
  const amount = rfq.total || rfq.estimated_total || rfq.final_total;

  // Safe image path resolution matching ProductCard.js
  const imgPath =
    rfq.items?.[0]?.item_image ||
    rfq.items?.[0]?.product_image ||
    rfq.items?.[0]?.image ||
    rfq.product_image_url ||
    rfq.product_image ||
    rfq.image;

  const imageUrl = imgPath
    ? imgPath.startsWith('http://') || imgPath.startsWith('https://') || imgPath.startsWith('data:')
      ? imgPath
      : `${IMAGE_BASE_URL}${imgPath}`
    : null;

  return (
    <div style={cardStyles.card} onClick={onPress}>
      {/* Top Row: RFQ Number, Status Tag, and Price */}
      <div style={cardStyles.cardTopRow}>
        <div style={cardStyles.tagsRow}>
          <span style={cardStyles.rfqNumberText}>{rfqNumber}</span>

          <div style={{ ...cardStyles.statusTag, backgroundColor: statusBg }}>
            <img src={StatusIcon} alt="status" style={{ width: 12, height: 12 }} />
            <span style={{ fontSize: 11, color: statusColor, fontWeight: '700' }}>
              {statusName}
            </span>
          </div>
        </div>

        {amount && (
          <span style={cardStyles.priceText}>
            ₹{Number(amount).toLocaleString('en-IN')}
          </span>
        )}
      </div>

      {/* Date */}
      <span style={cardStyles.dateText}>{formattedDate}</span>

      {/* Main Body Row: Image Box with exact ProductCard Tote fallback handling */}
      <div style={cardStyles.cardBodyRow}>
        <div style={cardStyles.imageBox}>
          {imageUrl && !imgError ? (
            <img
              src={imageUrl}
              alt={displayTitle}
              style={cardStyles.productImg}
              onError={() => setImgError(true)}
            />
          ) : (
            <div style={cardStyles.fallbackBox}>
              <img src={ToteIcon} alt="fallback" style={cardStyles.toteFallback} />
            </div>
          )}
        </div>

        <div style={cardStyles.metaColumn}>
          <span style={cardStyles.rfqTitle} title={displayTitle}>
            {displayTitle}
          </span>
          <span style={cardStyles.itemCountText}>
            {itemCount} {itemCount === 1 ? 'item requested' : 'items requested'}
          </span>
        </div>

        <div style={cardStyles.caretButton}>
          <img src={CaretRightRed} alt="open" style={{ width: 14, height: 14 }} />
        </div>
      </div>
    </div>
  );
}

const cardStyles = {
  card: {
    backgroundColor: '#FFFFFF',
    borderRadius: 12,
    border: '0.5px solid #CBD5E1',
    padding: 16,
    display: 'flex',
    flexDirection: 'column',
    gap: 10,
    cursor: 'pointer',
    boxSizing: 'border-box',
    transition: 'border-color 0.15s ease, box-shadow 0.15s ease',
    width: '100%',
  },
  cardTopRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  tagsRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
    flexWrap: 'wrap',
  },
  rfqNumberText: {
    fontSize: 13,
    fontWeight: '700',
    color: '#2E4057',
  },
  statusTag: {
    display: 'inline-flex',
    alignItems: 'center',
    borderRadius: 999,
    padding: '4px 8px',
    gap: 4,
  },
  priceText: {
    fontSize: 13.5,
    fontWeight: '700',
    color: '#2B2A29',
  },
  dateText: {
    fontSize: 11,
    color: '#707B8C',
  },
  cardBodyRow: {
    display: 'flex',
    alignItems: 'center',
    gap: 12,
    paddingTop: 2,
  },
  imageBox: {
    width: 48,
    height: 48,
    backgroundColor: '#FAFAF9',
    borderRadius: 8,
    border: '1px solid #E2E8F0',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    position: 'relative',
    overflow: 'hidden',
    flexShrink: 0,
  },
  productImg: {
    maxHeight: '85%',
    maxWidth: '85%',
    objectFit: 'contain',
  },
  fallbackBox: {
    width: '100%',
    height: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
  },
  toteFallback: {
    width: 24,
    height: 24,
    opacity: 0.35,
  },
  metaColumn: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    flex: 1,
    minWidth: 0,
  },
  rfqTitle: {
    fontSize: 13.5,
    fontWeight: '600',
    color: '#2B2A29',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  itemCountText: {
    fontSize: 11.5,
    color: '#707B8C',
    fontWeight: '500',
  },
  caretButton: {
    width: 24,
    height: 24,
    borderRadius: 12,
    backgroundColor: '#FFFFFF',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    flexShrink: 0,
    boxShadow: '0 1px 3px rgba(0,0,0,0.08)',
  },
};